import { useTranslation } from 'react-i18next';
import { usePricing, type PricingTier } from '@/services/pricing';

const TIER_ORDER = ['accessible', 'standard', 'premium'] as const;

function formatPrice(value: number | null, currency: string): string {
  if (value === null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  } catch {
    // Shouldn't happen (currency comes from the backend's own validated
    // config), but never let a formatting error take down the whole page.
    return `${value.toFixed(2)} ${currency}`;
  }
}

interface TierCardProps {
  tierKey: (typeof TIER_ORDER)[number];
  tier: PricingTier;
  currency: string;
}

function TierCard({ tierKey, tier, currency }: TierCardProps) {
  const { t } = useTranslation();
  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <p style={{ margin: '0 0 12px', fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-dark)' }}>
        {t(`subscription.tier_${tierKey}`)}
      </p>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '4px' }}>
        <span style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--primary, #4F46E5)' }}>
          {formatPrice(tier.public_price_yearly, currency)}
        </span>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{t('subscription.per_year')}</span>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
        {t('subscription.monthly')}: <strong>{formatPrice(tier.public_price_monthly, currency)}</strong>
        {t('subscription.per_month')}
      </p>

      <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-dark)' }}>
        {tier.session_limit === null
          ? t('subscription.unlimited_sessions')
          : t('subscription.sessions', { count: tier.session_limit })}
      </p>
    </div>
  );
}

interface SubscriptionTiersProps {
  countryCode: string | undefined;
}

export function SubscriptionTiers({ countryCode }: SubscriptionTiersProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = usePricing(countryCode);

  if (isLoading) {
    return <div className="loading-skeleton" style={{ height: '280px', borderRadius: '12px' }} />;
  }
  if (isError || !data) {
    return (
      <div className="card">
        <p style={{ margin: 0, color: 'var(--danger, #EF4444)', fontSize: '0.9rem' }}>
          {t('subscription.error')}
        </p>
      </div>
    );
  }

  const currency = data.currency ?? 'EUR';

  return (
    <div>
      {TIER_ORDER.map((tierKey) => (
        <TierCard key={tierKey} tierKey={tierKey} tier={data.tiers[tierKey]} currency={currency} />
      ))}
      <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
        {t('subscription.per_child_note')}
      </p>
    </div>
  );
}
